import type { PlanTier } from './featureCatalog';

/**
 * Starter (tier value 'Solo') includes the owner plus one Team Member seat: users holding any
 * role other than Team Member are limited to one plus the tenant's paid add-on seats. The backend
 * enforces this (SubscriptionDomainService.EnsureRolesAllowedForTierAsync); the UI mirrors it so
 * pickers can disable roles before the API rejects the save.
 */

export const TEAM_MEMBER_ROLE = 'Team Member';

/** Same text the backend returns when it rejects a role change. */
export const STARTER_TEAM_MEMBER_ONLY_MESSAGE = 'On the Starter plan, the second user can only be a Team Member.';

export interface StarterSeatMember {
  id?: string | null;
  roleNames?: Array<string> | null;
}

/** True when the roles are Team Member only (or empty). */
export function isTeamMemberOnly(roleNames: ReadonlyArray<string | null | undefined>): boolean {
  return roleNames
    .filter((name): name is string => !!name && name.trim() !== '')
    .every((name) => name.toLowerCase() === TEAM_MEMBER_ROLE.toLowerCase());
}

/**
 * Whether `userId` (undefined for a new user or invite) may hold `roleName` on this plan.
 */
export function isRoleAllowedOnPlan(params: {
  tier: PlanTier | null;
  additionalSeats: number;
  members: ReadonlyArray<StarterSeatMember>;
  roleName: string;
  userId?: string | null;
}): boolean {
  const { tier, additionalSeats, members, roleName, userId } = params;
  if (tier !== 'Solo' || isTeamMemberOnly([roleName])) {
    return true;
  }

  const otherElevatedUsers = members.filter(
    (m) => m.id !== userId && !isTeamMemberOnly(m.roleNames ?? []),
  ).length;
  return otherElevatedUsers < 1 + additionalSeats;
}
